import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  key: string;
}

/**
 * Advanced Caching Service
 * Provides multi-level caching with TTL, invalidation, and offline support
 */
@Injectable({ providedIn: 'root' })
export class CacheService {
  private readonly memoryCache = new Map<string, CacheEntry<any>>();
  private readonly indexedDBCache: IDBDatabase | null = null;
  private readonly maxMemorySize = 100; // Max entries in memory cache
  private readonly defaultTTL = 300000; // 5 minutes default TTL

  /**
   * Get data from cache
   */
  get<T = any>(key: string): Observable<T | null> {
    // Try memory cache first
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && this.isValid(memoryEntry)) {
      return of(memoryEntry.data as T);
    }

    // Try IndexedDB if available
    if (this.indexedDBCache) {
      return this.getFromIndexedDB<T>(key);
    }

    return of(null);
  }

  /**
   * Set data in cache
   */
  set<T = any>(key: string, data: T, ttl?: number): Observable<void> {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
      key,
    };

    // Store in memory
    this.memoryCache.set(key, entry);

    // Evict oldest entries if cache is full
    if (this.memoryCache.size > this.maxMemorySize) {
      this.evictOldest();
    }

    // Store in IndexedDB if available
    if (this.indexedDBCache) {
      return this.setInIndexedDB(key, entry).pipe(map(() => undefined));
    }

    return of(undefined);
  }

  /**
   * Remove entry from cache
   */
  remove(key: string): void {
    this.memoryCache.delete(key);
    if (this.indexedDBCache) {
      this.removeFromIndexedDB(key);
    }
  }

  /**
   * Clear all cache or entries matching pattern
   */
  clear(pattern?: string): void {
    if (pattern) {
      for (const key of this.memoryCache.keys()) {
        if (key.includes(pattern)) {
          this.memoryCache.delete(key);
        }
      }
    } else {
      this.memoryCache.clear();
    }

    if (this.indexedDBCache) {
      this.clearIndexedDB(pattern);
    }
  }

  /**
   * Check if entry is valid (not expired)
   */
  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  /**
   * Evict oldest entries
   */
  private evictOldest(): void {
    const entries = Array.from(this.memoryCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toEvict = entries.slice(0, Math.floor(this.maxMemorySize * 0.2)); // Evict 20%
    toEvict.forEach(([key]) => this.memoryCache.delete(key));
  }

  /**
   * Get from IndexedDB
   */
  private getFromIndexedDB<T>(key: string): Observable<T | null> {
    return new Observable((observer) => {
      if (!this.indexedDBCache) {
        observer.next(null);
        observer.complete();
        return;
      }

      const transaction = this.indexedDBCache.transaction(['cache'], 'readonly');
      const store = transaction.objectStore('cache');
      const request = store.get(key);

      request.onsuccess = () => {
        const entry: CacheEntry<T> | undefined = request.result;
        if (entry && this.isValid(entry)) {
          observer.next(entry.data);
        } else {
          observer.next(null);
        }
        observer.complete();
      };

      request.onerror = () => {
        observer.next(null);
        observer.complete();
      };
    });
  }

  /**
   * Set in IndexedDB
   */
  private setInIndexedDB<T>(key: string, entry: CacheEntry<T>): Observable<void> {
    return new Observable((observer) => {
      if (!this.indexedDBCache) {
        observer.next();
        observer.complete();
        return;
      }

      const transaction = this.indexedDBCache.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.put(entry);

      request.onsuccess = () => {
        observer.next();
        observer.complete();
      };

      request.onerror = () => {
        observer.error(request.error);
      };
    });
  }

  /**
   * Remove from IndexedDB
   */
  private removeFromIndexedDB(key: string): void {
    if (!this.indexedDBCache) return;

    const transaction = this.indexedDBCache.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    store.delete(key);
  }

  /**
   * Clear IndexedDB
   */
  private clearIndexedDB(pattern?: string): void {
    if (!this.indexedDBCache) return;

    const transaction = this.indexedDBCache.transaction(['cache'], 'readwrite');
    const store = transaction.objectStore('cache');
    
    if (pattern) {
      const request = store.openCursor();
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.key.includes(pattern)) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    } else {
      store.clear();
    }
  }

  /**
   * Get cache stats
   */
  getStats(): {
    memorySize: number;
    memoryEntries: number;
    indexedDBAvailable: boolean;
  } {
    return {
      memorySize: this.memoryCache.size,
      memoryEntries: this.memoryCache.size,
      indexedDBAvailable: !!this.indexedDBCache,
    };
  }
}

